import { useState, useEffect, useRef } from 'react'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { api } from '../../utils/api'
import { Search, Loader2, Hash, AlertTriangle, CheckCircle2, ChevronRight, ShieldAlert, Eye } from 'lucide-react'
import toast from 'react-hot-toast'
import { is503, getErrorDetail } from '../../utils/aiError'
import { AiErrorBlock } from './AiErrorBlock'
import { ReviewGate } from '../ReviewGate'
import { AiPreviewItemCard, type RackOption, type CatalogOption } from './AiPreviewItemCard'
import { SerialVerificationBlock } from './SerialVerificationBlock'
import { RackDiffPreview } from './RackDiffPreview'
import { buildReviewOperations, collectConflicts, isPhysicallyBlocked, isAdvisoryOnly, canOpenReviewGate, physicsChanged, jiraManualItems } from './aiPreview.logic'
import { ruPlural } from '../woLifecycle.logic'
import { rackLabel } from '../../utils/rackName.logic'
import type { WorkOrderPreview, WorkOrderItemPreview } from '../../types/workOrder'

interface JiraTaskSelectorProps {
  isOpen: boolean
  onClose: () => void
  /** Вызывается когда инженер подтвердил preview наряда */
  onConfirmPreview: (preview: WorkOrderPreview, dcCode?: string) => void
  defaultDcCode?: string
}

interface JiraIssue {
  key: string
  fields: {
    summary: string
    status: { name: string }
    assignee?: { displayName: string }
  }
}

type TabMode = 'search' | 'direct'

export function JiraTaskSelector({
  isOpen,
  onClose,
  onConfirmPreview,
  defaultDcCode,
}: JiraTaskSelectorProps) {
  const [tab, setTab] = useState<TabMode>('direct')
  const [query, setQuery] = useState('')
  const [ticketId, setTicketId] = useState('')
  const [dcCode, setDcCode] = useState(defaultDcCode || '')
  const [results, setResults] = useState<JiraIssue[]>([])
  const [preview, setPreview] = useState<WorkOrderPreview | null>(null)
  const [loading, setLoading] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)
  const [showReviewGate, setShowReviewGate] = useState(false)
  const [racks, setRacks] = useState<RackOption[]>([])
  const [catalog, setCatalog] = useState<CatalogOption[]>([])
  const recheckSeq = useRef(0)
  const [recheckPending, setRecheckPending] = useState(false)
  const [manualMode, setManualMode] = useState(false)
  const [manualTitle, setManualTitle] = useState('')
  const [manualDescription, setManualDescription] = useState('')

  useEffect(() => {
    if (isOpen) {
      setQuery('')
      setTicketId('')
      setPreview(null)
      setResults([])
      setAiError(null)
      setShowReviewGate(false)
      setManualMode(false)
      setRecheckPending(false)
      if (tab === 'search') searchJira('')
    }
  }, [isOpen])

  useEffect(() => {
    if (defaultDcCode) setDcCode(defaultDcCode)
  }, [defaultDcCode])

  // Справочники для inline-резолва — грузим при появлении превью с известным ЦОДом
  useEffect(() => {
    const code = preview?.datacenter_code || dcCode
    if (!preview || !code) return
    if (racks.length === 0) {
      api.get(`/api/datacenters/${code}/racks`)
        .then(res => setRacks(
          (res.data || []).map((r: any) => ({ id: r.id, label: rackLabel(r) }))
        ))
        .catch(() => {})
    }
    if (catalog.length === 0) {
      api.get('/api/equipment/catalog', { params: { limit: 200 } })
        .then(res => setCatalog(
          (res.data || []).map((c: any) => ({
            id: c.id,
            label: `${c.manufacturer} ${c.model} (${c.u_height}U)`,
            u_height: c.u_height,
          }))
        ))
        .catch(() => {})
    }
  }, [preview])

  const searchJira = async (q: string) => {
    setLoading(true)
    try {
      const res = await api.get(`/api/integrations/jira/search`, { params: { q } })
      setResults(res.data.issues || [])
    } catch (err: any) {
      toast.error('Ошибка поиска в Jira: ' + getErrorDetail(err))
    } finally {
      setLoading(false)
    }
  }

  const fetchPreview = async (tid: string) => {
    if (!tid.trim()) {
      toast.error('Введите номер задачи')
      return
    }
    setLoading(true)
    setPreview(null)
    setAiError(null)
    try {
      const res = await api.post(`/api/integrations/jira/import-issue`, {
        ticket_id: tid.trim().toUpperCase(),
        datacenter_code: dcCode || undefined,
      })
      setRecheckPending(false)
      setPreview(res.data)
    } catch (err: any) {
      if (is503(err)) {
        setAiError(getErrorDetail(err))
        const data = err.response?.data || {}
        setManualTitle(data.jira_title || tid.trim().toUpperCase())
        setManualDescription(data.jira_description || '')
      } else {
        toast.error('Ошибка импорта: ' + getErrorDetail(err))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleItemChange = async (index: number, updated: WorkOrderItemPreview) => {
    if (!preview) return
    const prev = preview.items[index]
    const items = preview.items.map((it, i) => (i === index ? updated : it))
    const code = preview.datacenter_code || dcCode
    if (!code || !physicsChanged(prev, updated)) {
      setPreview({ ...preview, items })
      return
    }
    setRecheckPending(true)
    setPreview({ ...preview, items })
    const seq = ++recheckSeq.current
    try {
      const res = await api.post(
        `/api/datacenters/${code}/impact-analysis/work-orders/validate-preview`,
        { items },
      )
      if (seq !== recheckSeq.current) return
      setRecheckPending(false)
      setPreview(p => (p ? {
        ...p,
        items: res.data.items,
        commit_block_reason: res.data.commit_block_reason ?? null,
      } : p))
    } catch {
      // fail-closed: leave recheckPending so Create stays off until a later successful recheck
    }
  }

  /** Реальное подтверждение — вызывается только из ReviewGate.onConfirm. */
  const handleConfirm = () => {
    if (!preview) return
    setShowReviewGate(false)
    onConfirmPreview(preview, preview.datacenter_code || dcCode)
    onClose()
  }

  const openManualForm = () => {
    setManualMode(true)
    setAiError(null)
    if (!manualTitle.trim()) {
      setManualTitle(ticketId.trim().toUpperCase() || 'Jira')
    }
  }

  const handleManualCreate = () => {
    if (!dcCode) {
      toast.error('Укажите код ЦОДа')
      return
    }
    if (!manualTitle.trim()) {
      toast.error('Укажите заголовок наряда')
      return
    }
    const ticket = ticketId.trim().toUpperCase() || undefined
    onConfirmPreview({
      title: manualTitle.trim(),
      description: manualDescription,
      datacenter_code: dcCode,
      jira_ticket_id: ticket,
      items: jiraManualItems(manualDescription || manualTitle),
    }, dcCode)
    onClose()
  }

  const unresolvedCount = preview?.items.filter(i => i.has_unresolved).length || 0
  const blockedCount = preview?.items.filter(isPhysicallyBlocked).length || 0
  const advisoryOnly = isAdvisoryOnly(preview?.allowed_autonomy_level)
  const conflicts = preview ? collectConflicts(preview.items) : []
  const reviewReady = preview ? canOpenReviewGate(preview.commit_block_reason) : false

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Импорт задачи из Jira" size="xl">
      {/* Tabs */}
      <div className="flex border-b border-border/50 mb-4 -mx-1">
        <button
          onClick={() => { setTab('direct'); setPreview(null) }}
          className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 -mb-px ${
            tab === 'direct'
              ? 'border-accent text-accent'
              : 'border-transparent text-text-muted hover:text-text-primary'
          }`}
        >
          <Hash className="w-3.5 h-3.5 inline mr-1.5" />
          По номеру задачи
        </button>
        <button
          onClick={() => { setTab('search'); setPreview(null); searchJira('') }}
          className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 -mb-px ${
            tab === 'search'
              ? 'border-accent text-accent'
              : 'border-transparent text-text-muted hover:text-text-primary'
          }`}
        >
          <Search className="w-3.5 h-3.5 inline mr-1.5" />
          Поиск по JQL
        </button>
      </div>

      {/* Direct input tab */}
      {tab === 'direct' && !preview && !manualMode && !aiError && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                placeholder="Номер задачи: DC-184, CORERFS-13..."
                value={ticketId}
                onChange={e => setTicketId(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fetchPreview(ticketId)}
              />
            </div>
            <Button onClick={() => fetchPreview(ticketId)} disabled={loading || !ticketId.trim()} aria-label="Показать превью задачи">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
            </Button>
          </div>
          <div className="flex gap-2 items-center">
            <Input
              placeholder="Код ЦОДа (опционально, напр. dc-alpha)"
              value={dcCode}
              onChange={e => setDcCode(e.target.value)}
            />
          </div>
          <p className="text-xs text-text-muted">
            AI проанализирует контекст задачи и подготовит структуру наряда для проверки инженером.
          </p>
        </div>
      )}

      {/* Search tab */}
      {tab === 'search' && !preview && !manualMode && !aiError && (
        <div>
          <form onSubmit={e => { e.preventDefault(); searchJira(query) }} className="flex gap-2 mb-4">
            <div className="flex-1">
              <Input
                placeholder="JQL запрос (оставьте пустым для своих задач)..."
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={loading} aria-label="Искать задачи">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </form>

          <div className="space-y-2 max-h-[350px] overflow-y-auto pr-2">
            {!loading && results.length === 0 && (
              <div className="text-center text-text-muted py-8">Задачи не найдены</div>
            )}
            {results.map(issue => (
              <div
                key={issue.key}
                className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-bg-panel/40 hover:bg-bg-input/50 cursor-pointer transition-colors"
                onClick={() => {
                  setTicketId(issue.key)
                  setTab('direct')
                  fetchPreview(issue.key)
                }}
              >
                <div>
                  <div className="font-bold text-accent">{issue.key}</div>
                  <div className="text-sm text-text-primary">{issue.fields.summary}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-text-muted">{issue.fields.status?.name}</div>
                  <div className="text-xs text-text-muted">{issue.fields.assignee?.displayName || 'Не назначен'}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 503 AI Error — shown instead of preview when AI returned 503 */}
      {aiError && !preview && !manualMode && (
        <AiErrorBlock
          detail={aiError}
          onRetry={() => { setAiError(null); fetchPreview(ticketId) }}
          onClose={onClose}
          onManual={openManualForm}
        />
      )}

      {manualMode && !preview && (
        <div className="space-y-4" data-testid="manual-create-form">
          <p className="text-sm text-text-secondary">
            AI недоступен — наряд будет создан вручную с текстом задачи Jira.
          </p>
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">Заголовок наряда</label>
            <input
              value={manualTitle}
              onChange={e => setManualTitle(e.target.value)}
              className="w-full px-4 py-2.5 bg-bg-input text-text-primary rounded-xl border border-border/60
                focus:border-accent focus:ring-1 focus:ring-accent/30 outline-none text-sm"
              data-testid="manual-title"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">Описание</label>
            <textarea
              value={manualDescription}
              onChange={e => setManualDescription(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 bg-bg-input text-text-primary rounded-xl border border-border/60
                focus:border-accent focus:ring-1 focus:ring-accent/30 outline-none resize-none text-sm"
              data-testid="manual-description"
            />
          </div>
          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => setManualMode(false)} className="flex-1">
              Назад
            </Button>
            <Button
              onClick={handleManualCreate}
              disabled={!manualTitle.trim()}
              className="flex-1 bg-accent hover:bg-accent/90"
              data-testid="manual-create-submit"
            >
              Создать наряд
            </Button>
          </div>
        </div>
      )}

      {preview && (
        <div className="space-y-4">
          {/* Jira metadata */}
          <div className="rounded-xl border border-accent/20 bg-accent/5 p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-xs px-2 py-0.5 bg-accent/20 text-accent rounded">
                    {preview.jira_ticket_id}
                  </span>
                  {(preview.jira_metadata as any)?.status && (
                    <span className="text-xs text-text-muted">{(preview.jira_metadata as any).status}</span>
                  )}
                </div>
                <h4 className="font-bold text-text-primary">{preview.title}</h4>
                {(preview.jira_metadata as any)?.assignee && (
                  <p className="text-xs text-text-muted mt-1">
                    Назначен: {(preview.jira_metadata as any).assignee}
                  </p>
                )}
              </div>
              <button
                onClick={() => setPreview(null)}
                className="text-text-muted hover:text-text-primary text-xs underline"
              >
                Изменить
              </button>
            </div>

            {preview.datacenter_code && (
              <div className="mt-2 text-xs text-text-muted">
                ЦОД: <span className="text-accent font-mono">{preview.datacenter_code}</span>
              </div>
            )}
          </div>

          {/* Autonomy level 1: advisory only */}
          {advisoryOnly && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-accent/10 border border-accent/30" data-testid="advisory-banner">
              <Eye className="w-4 h-4 text-accent shrink-0 mt-0.5" />
              <div className="text-sm text-text-primary">
                AI работает в режиме <strong>советника</strong> (уровень автономии 1).
                Создание наряда из этого превью отключено администратором.
              </div>
            </div>
          )}

          {/* Physical conflicts */}
          {blockedCount > 0 && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-status-critical/10 border border-status-critical/30" data-testid="physical-conflicts-banner">
              <ShieldAlert className="w-4 h-4 text-status-critical shrink-0 mt-0.5" />
              <div className="text-sm text-status-critical">
                <strong>{ruPlural(blockedCount, 'шаг', 'шага', 'шагов')}</strong> физически невыполнимы (занятый юнит/порт или объект не найден).
                Исправьте их через выпадающие списки ниже — иначе создание заблокировано.
              </div>
            </div>
          )}

          {/* Unresolved warning */}
          {unresolvedCount > 0 && blockedCount === 0 && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-status-warning/10 border border-status-warning/20">
              <AlertTriangle className="w-4 h-4 text-status-warning shrink-0 mt-0.5" />
              <div className="text-sm text-status-warning">
                <strong>{ruPlural(unresolvedCount, 'шаг', 'шага', 'шагов')}</strong> требуют уточнения (стойка, модель или порт не определены).
                Выберите значения в выпадающих списках ниже.
              </div>
            </div>
          )}

          {/* Serial verification (P1-5) */}
          <SerialVerificationBlock rows={preview.serial_verification} />

          {(preview.datacenter_code || dcCode) && (
            <RackDiffPreview dcCode={preview.datacenter_code || dcCode} items={preview.items} />
          )}

          {/* AI notes */}
          {preview.unresolved_notes && (
            <div className="p-3 rounded-lg bg-bg-input/50 border border-border/40">
              <p className="text-xs text-text-muted font-semibold mb-1">Заметки AI:</p>
              <p className="text-sm text-text-secondary">{preview.unresolved_notes}</p>
            </div>
          )}

          {/* Items preview */}
          <div>
            <p className="text-xs font-bold text-text-muted uppercase tracking-widest mb-3">
              Шаги наряда ({preview.items.length})
            </p>
            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
              {preview.items.length === 0 ? (
                <p className="text-sm text-text-muted italic">AI не смог определить шаги. Создайте наряд вручную.</p>
              ) : (
                preview.items.map((item, idx) => (
                  <AiPreviewItemCard
                    key={idx}
                    item={item}
                    index={idx}
                    racks={racks}
                    catalog={catalog}
                    onChange={handleItemChange}
                  />
                ))
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2 border-t border-border/30">
            <Button variant="ghost" onClick={() => setPreview(null)} className="flex-1">
              Назад
            </Button>
            {!advisoryOnly && (
              <Button
                onClick={() => setShowReviewGate(true)}
                className="flex-1 bg-accent hover:bg-accent/90"
                disabled={(!preview.datacenter_code && !dcCode) || !reviewReady || recheckPending}
                data-testid="open-review-gate"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Проверить и создать
              </Button>
            )}
          </div>
          {preview.commit_block_reason && (
            <p className="text-xs text-status-critical pt-2" data-testid="commit-block-reason">
              {preview.commit_block_reason}
            </p>
          )}
          {!preview.datacenter_code && !dcCode && (
            <p className="text-xs text-status-critical text-center">
              Укажите код ЦОДа для создания наряда
            </p>
          )}
        </div>
      )}

      {/* Human Review Gate (P1-3): блокирует подтверждение до явного «Ознакомлен» */}
      {showReviewGate && preview && preview.items.length > 0 && (
        <ReviewGate
          title="Подтверждение наряда из Jira"
          operations={buildReviewOperations(preview.items)}
          conflicts={conflicts}
          onConfirm={handleConfirm}
          onCancel={() => setShowReviewGate(false)}
          preview={
            (preview.datacenter_code || dcCode)
              ? <RackDiffPreview dcCode={preview.datacenter_code || dcCode} items={preview.items} />
              : undefined
          }
        />
      )}
    </Modal>
  )
}
